// Voxel raycasting via the Amanatides-Woo 3D-DDA algorithm. Walks the
// grid one cell at a time in ray-direction order, returning the first
// occupied cell hit along with which of its six faces the ray entered
// through.
//
// Coordinates are grid-local: the ray's origin and direction must already
// be in the same frame as VoxelGrid cells (i.e. cell (ix,iy,iz) occupies
// the box [ix*CELL_SIZE, (ix+1)*CELL_SIZE] etc.). Callers responsible for
// applying any parent-group inverse transform before calling.

import * as THREE from "three";
import { CELL_SIZE, VoxelGrid } from "./voxel-grid";

// Face indices. Pair (i, i^1) are opposite faces — e.g. 0 (-X) and 1 (+X).
const FACE_NEG_X = 0 as const;
const FACE_POS_X = 1 as const;
const FACE_NEG_Y = 2 as const;
const FACE_POS_Y = 3 as const;
const FACE_NEG_Z = 4 as const;
const FACE_POS_Z = 5 as const;

export type FaceNormal = 0 | 1 | 2 | 3 | 4 | 5;

export interface VoxelHit {
  ix: number;
  iy: number;
  iz: number;
  // Which face of (ix,iy,iz) the ray entered through. Use
  // faceNormalVec(face) to get the outward normal as ±1 axis components.
  face: FaceNormal;
}

// Outward normal as (nx, ny, nz) ∈ {-1, 0, +1}^3 with exactly one nonzero.
export function faceNormal(face: FaceNormal): [number, number, number] {
  switch (face) {
    case FACE_NEG_X:
      return [-1, 0, 0];
    case FACE_POS_X:
      return [1, 0, 0];
    case FACE_NEG_Y:
      return [0, -1, 0];
    case FACE_POS_Y:
      return [0, 1, 0];
    case FACE_NEG_Z:
      return [0, 0, -1];
    case FACE_POS_Z:
      return [0, 0, 1];
  }
}

// Returns the first filled cell the ray enters, or null if no cell is hit
// within `maxCells` steps. `maxCells` should be set well above the
// expected grid diameter — for a CELL_SIZE of 0.1m, 4096 covers ~400m of
// traversal in the worst case.
//
// `skip`, when provided, names cells (3D "ix,iy,iz" keys) that the
// raycast treats as empty even if `grid.has` reports them filled. Used
// by drag-paint to make the laser "see through" voxels it just placed,
// so the cursor stays on the original surface and re-strikes the same
// pick frame after frame instead of climbing onto the new geometry.
//
// `include` is the inverse: names cells the raycast treats as
// OCCUPIED even when they're empty in the grid. Used by box-erase so
// the laser stops at cells it has already erased — the cursor stays
// on the deleted face instead of drilling through to the voxel behind.
// An inclusive cell-coord box the raycast can treat as occupied (the region-
// based form of `include`): box-erase passes the region it has already carved so
// the laser stops at the original surface instead of drilling into the cavity —
// without building a per-cell set of the (potentially millions of) erased cells.
export interface CellBox {
  x0: number; x1: number; y0: number; y1: number; z0: number; z1: number;
}

export function raycastVoxelGrid(
  grid: VoxelGrid,
  originLocal: THREE.Vector3,
  dirLocal: THREE.Vector3,
  maxCells = 4096,
  skip?: ReadonlySet<string>,
  include?: ReadonlySet<string>,
  includeBox?: CellBox | null,
  skipBox?: CellBox | null,
): VoxelHit | null {
  const dx = dirLocal.x;
  const dy = dirLocal.y;
  const dz = dirLocal.z;

  let ix = Math.floor(originLocal.x / CELL_SIZE);
  let iy = Math.floor(originLocal.y / CELL_SIZE);
  let iz = Math.floor(originLocal.z / CELL_SIZE);

  const inIncludeBox = (x: number, y: number, z: number): boolean =>
    includeBox != null &&
    x >= includeBox.x0 && x <= includeBox.x1 &&
    y >= includeBox.y0 && y <= includeBox.y1 &&
    z >= includeBox.z0 && z <= includeBox.z1;

  // The inverse of includeBox: cells treated as EMPTY even when filled. A live
  // box_draw passes the box it's filling here so the ray looks PAST it to the
  // original surface (otherwise the drawn box deflects its own target cell).
  const inSkipBox = (x: number, y: number, z: number): boolean =>
    skipBox != null &&
    x >= skipBox.x0 && x <= skipBox.x1 &&
    y >= skipBox.y0 && y <= skipBox.y1 &&
    z >= skipBox.z0 && z <= skipBox.z1;

  const occupied = (x: number, y: number, z: number): boolean => {
    if (inSkipBox(x, y, z)) return false;
    if (inIncludeBox(x, y, z)) return true;
    const key = include || skip ? `${x},${y},${z}` : "";
    if (include && include.has(key)) return true;
    if (!grid.has(x, y, z)) return false;
    if (skip && skip.has(key)) return false;
    return true;
  };

  // If the ray starts inside a filled cell, treat it as a hit with the
  // face most opposed to the ray direction (best guess at "the face the
  // user is pointing at from outside this volume"). In practice the user
  // is always outside any solid before clicking, so this branch is rare.
  if (occupied(ix, iy, iz)) {
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    const az = Math.abs(dz);
    let face: FaceNormal;
    if (ax >= ay && ax >= az) face = dx > 0 ? FACE_NEG_X : FACE_POS_X;
    else if (ay >= az) face = dy > 0 ? FACE_NEG_Y : FACE_POS_Y;
    else face = dz > 0 ? FACE_NEG_Z : FACE_POS_Z;
    return { ix, iy, iz, face };
  }

  const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;
  const stepZ = dz > 0 ? 1 : dz < 0 ? -1 : 0;

  const nextBoundaryX =
    stepX > 0 ? (ix + 1) * CELL_SIZE : stepX < 0 ? ix * CELL_SIZE : 0;
  const nextBoundaryY =
    stepY > 0 ? (iy + 1) * CELL_SIZE : stepY < 0 ? iy * CELL_SIZE : 0;
  const nextBoundaryZ =
    stepZ > 0 ? (iz + 1) * CELL_SIZE : stepZ < 0 ? iz * CELL_SIZE : 0;

  let tMaxX =
    stepX !== 0 ? (nextBoundaryX - originLocal.x) / dx : Number.POSITIVE_INFINITY;
  let tMaxY =
    stepY !== 0 ? (nextBoundaryY - originLocal.y) / dy : Number.POSITIVE_INFINITY;
  let tMaxZ =
    stepZ !== 0 ? (nextBoundaryZ - originLocal.z) / dz : Number.POSITIVE_INFINITY;

  const tDeltaX = stepX !== 0 ? Math.abs(CELL_SIZE / dx) : Number.POSITIVE_INFINITY;
  const tDeltaY = stepY !== 0 ? Math.abs(CELL_SIZE / dy) : Number.POSITIVE_INFINITY;
  const tDeltaZ = stepZ !== 0 ? Math.abs(CELL_SIZE / dz) : Number.POSITIVE_INFINITY;

  for (let i = 0; i < maxCells; i++) {
    // Step along whichever axis hits its next cell boundary first. The
    // axis we step on determines which face of the *new* cell we enter
    // through — if we stepped +X, we entered the new cell through its -X
    // face, and vice versa.
    let enteredFace: FaceNormal;
    if (tMaxX < tMaxY && tMaxX < tMaxZ) {
      ix += stepX;
      tMaxX += tDeltaX;
      enteredFace = stepX > 0 ? FACE_NEG_X : FACE_POS_X;
    } else if (tMaxY < tMaxZ) {
      iy += stepY;
      tMaxY += tDeltaY;
      enteredFace = stepY > 0 ? FACE_NEG_Y : FACE_POS_Y;
    } else {
      iz += stepZ;
      tMaxZ += tDeltaZ;
      enteredFace = stepZ > 0 ? FACE_NEG_Z : FACE_POS_Z;
    }
    if (occupied(ix, iy, iz)) {
      return { ix, iy, iz, face: enteredFace };
    }
  }
  return null;
}
